<template>
  <div class="dashboard-card" @click="$emit('click')">
    <div class="card-preview">
      <div
        v-if="previewSlots.length"
        class="preview-grid"
        :class="'preview-grid--' + previewSlots.length"
      >
        <div
          v-for="(slot, index) in previewSlots"
          :key="'plot-' + index"
          class="preview-item"
        >
          <MiniPlotPreview :plot-config="slot" />
        </div>
      </div>
    </div>
    
    <div class="card-content">
      <div class="card-info">
        <h3 class="card-title">{{ dashboard.title }}</h3>
        <div class="card-stats">
          <div class="stat-item">
            <v-icon size="17" class="stat-icon">mdi-view-grid</v-icon>
            <span class="stat-text">{{ dashboard.plotCount }}/{{ dashboard.maxPlots }}</span>
          </div>
          <div class="stat-item" v-if="dashboard.shared">
            <v-icon size="17" class="stat-icon">mdi-earth</v-icon>
            <span class="stat-text">Shared</span>
          </div>
        </div>
      </div>
      <v-menu
        location="bottom end"
        :close-on-content-click="false"
        transition="scale-transition"
        content-class="card-menu-overlay"
        offset="10"
      >
        <template #activator="{ props: menuProps }">
          <button
            class="card-menu-btn"
            v-bind="menuProps"
            @click.stop
            aria-label="Dashboard options"
          >
            <v-icon size="22">mdi-dots-vertical</v-icon>
          </button>
        </template>
        <div class="card-menu-panel">
          <button
            v-if="canEdit"
            type="button"
            class="card-menu-action"
            @click.stop="$emit('edit')"
          >
            <span class="card-menu-icon card-menu-icon--edit">
              <v-icon size="16">mdi-pencil-outline</v-icon>
            </span>
            <span class="card-menu-label">
              <span class="card-menu-label-title">Edit</span>
              <span class="card-menu-label-sub">Open dashboard</span>
            </span>
          </button>
          <div v-if="canEdit && canShare" class="card-menu-divider" role="separator"></div>
          <div v-if="canShare" class="card-menu-share" @click.stop>
            <span class="card-menu-share-label">Visibility</span>
            <div class="tab-div dash-access-toggle card-menu-access-toggle">
              <v-btn-toggle
                :model-value="isPublic"
                class="axis-toggle font-overlay"
                mandatory
                density="compact"
                @update:model-value="handleShareToggle"
              >
                <v-btn :value="false" class="switch-btn" outlined>My Dashboard</v-btn>
                <v-btn :value="true" class="switch-btn" outlined>Shared</v-btn>
              </v-btn-toggle>
            </div>
          </div>
          <div v-if="canDelete" class="card-menu-divider" role="separator"></div>
          <button
            v-if="canDelete"
            type="button"
            class="card-menu-action card-menu-action--delete"
            @click.stop="$emit('delete')"
          >
            <span class="card-menu-icon card-menu-icon--delete">
              <v-icon size="16">mdi-trash-can-outline</v-icon>
            </span>
            <span class="card-menu-label">
              <span class="card-menu-label-title">Delete</span>
              <span class="card-menu-label-sub">Remove permanently</span>
            </span>
          </button>
        </div>
      </v-menu>
    </div>

    <button
      v-if="canStar"
      class="star-btn"
      :class="{ 'star-btn--active': dashboard.isStarred }"
      @click.stop="$emit('star')"
      :title="dashboard.isStarred ? 'Remove from starred' : 'Add to starred'"
      :aria-label="dashboard.isStarred ? 'Unstar dashboard' : 'Star dashboard'"
    >
      <img
        :src="dashboard.isStarred ? starFilledIcon : starEmptyIcon"
        alt=""
        class="star-icon"
      />
    </button>
  </div>
</template>

<script>
import MiniPlotPreview from "@/components/dashboard/MiniPlotPreview.vue";

export default {
  name: "DashboardCard",
  components: { MiniPlotPreview },
  props: {
    dashboard: {
      type: Object,
      required: true,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    canStar: {
      type: Boolean,
      default: true,
    },
    canShare: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    isPublic: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["click", "star", "edit", "delete", "share-change"],
  data() {
    return {
      starEmptyIcon: require("@/assets/Images/Dashboard/star.svg"),
      starFilledIcon: require("@/assets/Svg/Dashboard/small-filled-star.svg"),
    };
  },
  computed: {
    /** Up to 4 `{ data, chartData }` entries — only real plots (no placeholder cells). */
    previewSlots() {
      const plots = this.dashboard.previewPlotConfigs || [];
      return plots.filter(Boolean).slice(0, 4);
    },
  },
  methods: {
    handleShareToggle(value) {
      this.$emit("share-change", value);
    },
  },
};
</script>

<style scoped>
@import '../../assets/Styling/agenticDashboard/dashboardCard.css';
</style>

<!-- Teleported v-menu content lives outside the card; keep these global -->
<style>
.card-menu-overlay {
  box-shadow: none !important;
  background: transparent !important;
  overflow: visible !important;
}

.card-menu-panel {
  min-width: 240px;
  padding: 6px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(34, 152, 213, 0.12);
  box-shadow:
    0 4px 6px rgba(6, 45, 66, 0.06),
    0 12px 28px rgba(6, 45, 66, 0.14);
  backdrop-filter: blur(12px);
}

.card-menu-action {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.18s ease, transform 0.12s ease;
}

.card-menu-action:hover {
  background: rgba(34, 152, 213, 0.08);
}

.card-menu-action:active {
  transform: scale(0.98);
}

.card-menu-action--delete:hover {
  background: rgba(211, 47, 47, 0.08);
}

.card-menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}

.card-menu-icon--edit {
  background: rgba(34, 152, 213, 0.12);
  color: #1a7eb5;
}

.card-menu-icon--delete {
  background: rgba(211, 47, 47, 0.1);
  color: #c62828;
}

.card-menu-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.card-menu-label-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: #1a2b3c;
  letter-spacing: -0.01em;
}

.card-menu-action--delete .card-menu-label-title {
  color: #b71c1c;
}

.card-menu-label-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.2;
  color: #6b7c8f;
}

.card-menu-divider {
  height: 1px;
  margin: 4px 8px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(34, 152, 213, 0.18) 20%,
    rgba(34, 152, 213, 0.18) 80%,
    transparent
  );
}

.card-menu-share {
  padding: 8px 10px 10px;
}

.card-menu-share-label {
  display: block;
  margin-bottom: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7c8f;
}

.card-menu-access-toggle {
  width: 100%;
}

.card-menu-access-toggle .v-btn-group {
  width: 100% !important;
}

.card-menu-access-toggle .switch-btn {
  flex: 1 1 50%;
  min-width: 120px !important;
  white-space: nowrap;
  padding: 0 12px !important;
  font-size: 11px !important;
  overflow: visible !important;
}

.card-menu-access-toggle .switch-btn .v-btn__content {
  overflow: visible !important;
  justify-content: center;
}

.dark-mode .card-menu-panel {
  background: rgba(28, 36, 48, 0.96);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.2),
    0 12px 28px rgba(0, 0, 0, 0.35);
}

.dark-mode .card-menu-label-title {
  color: #e8eef4;
}

.dark-mode .card-menu-action--delete .card-menu-label-title {
  color: #ef9a9a;
}

.dark-mode .card-menu-label-sub {
  color: #9aa8b8;
}

.dark-mode .card-menu-share-label {
  color: #9aa8b8;
}

.dark-mode .card-menu-action:hover {
  background: rgba(34, 152, 213, 0.16);
}

.dark-mode .card-menu-action--delete:hover {
  background: rgba(211, 47, 47, 0.16);
}

.dark-mode .card-menu-icon--edit {
  background: rgba(34, 152, 213, 0.22);
  color: #7ec8ef;
}

.dark-mode .card-menu-icon--delete {
  background: rgba(211, 47, 47, 0.2);
  color: #ef9a9a;
}
</style>

